/**
 * +field: Mossvale ids (docs/design/zones.md Phase 1) — a SHARED CONTRACT like ../ids.ts: sim, web,
 * progression and the renderer rely on these strings. Add freely; never rename.
 */

export const ZONE = {
  mossvale: 'mossvale',
} as const;

/** Map ids (DungeonDef ids in the main bundle). */
export const MAP = {
  /** +zone: the whole Mossvale zone, one seamless open map (docs/design/zones.md "Mossvale"). */
  mossvale: 'mossvale',
  /** Phase 1 maps, merged into `mossvale` (kept for save migration; no longer in the bundle). */
  mossvaleTown: 'mossvale_town',
  sunmeadow: 'sunmeadow',
  bramblewood: 'bramblewood',
  /** The starter dungeon under Sunmeadow (levelled, mobLevel 4). */
  hollowBarrow: 'hollow_barrow',
} as const;

/** Mob NpcDef ids (fields + the Hollow Barrow). */
export const MOB = {
  /** +specs: Warden Ilsa's practice dummy (the Advancement Trial's "try it"). */
  practiceDummy: 'tower_practice_dummy',
  // Sunmeadow (1–5)
  meadowSlime: 'meadow_slime',
  bloomSlime: 'bloom_slime',
  tuskBoar: 'tusk_boar',
  // Bramblewood (5–10)
  brambleWolf: 'bramble_wolf',
  briarBandit: 'briar_bandit',
  banditSlinger: 'bandit_slinger',
  banditCaptain: 'bandit_captain',
  // The Hollow Barrow
  barrowGhoul: 'barrow_ghoul',
  barrowWisp: 'barrow_wisp',
  barrowCrawler: 'barrow_crawler',
  barrowBonecaller: 'barrow_bonecaller',
  barrowKing: 'barrow_king',
  /** Every field: the rare fleeing loot sprite (FieldDef.loot.spriteNpcId). */
  treasureSprite: 'treasure_sprite',
  // +zone: Glimmermere (3–5)
  reedbackSnapper: 'reedback_snapper',
  mireCroaker: 'mire_croaker',
  // +zone: Greyhorn Heights (4–7)
  cragHound: 'crag_hound',
  tallowDigger: 'tallow_digger',
  tallowChanter: 'tallow_chanter',
  // +zone: the Pale Rise (8–10)
  paleSentinel: 'pale_sentinel',
  hollowShade: 'hollow_shade',
  // +zone: rare named mobs (one per region, long timers)
  oldMossback: 'old_mossback',
  oldSnapjaw: 'old_snapjaw',
  foremanTallowick: 'foreman_tallowick',
  greymaw: 'greymaw',
  paleWarden: 'pale_warden',
} as const;

/** Mob enemy-ability ids. */
export const MOB_ABILITY = {
  pollenSpit: 'mob.bloom_slime.pollen_spit',
  tuskCharge: 'mob.tusk_boar.tusk_charge',
  lungingBite: 'mob.bramble_wolf.lunging_bite',
  dirtySlash: 'mob.briar_bandit.dirty_slash',
  slingStone: 'mob.bandit_slinger.sling_stone',
  brutalSweep: 'mob.bandit_captain.brutal_sweep',
  rallyingBellow: 'mob.bandit_captain.rallying_bellow',
  graveBolt: 'mob.barrow_wisp.grave_bolt',
  carrionGnaw: 'mob.barrow_crawler.carrion_gnaw',
  boneSpikes: 'barrow.bonecaller.bone_spikes',
  boneShard: 'barrow.bonecaller.bone_shard',
  graveCleave: 'barrow.king.grave_cleave',
  callOfTheBarrow: 'barrow.king.call_of_the_barrow',
  // +zone
  clawSnap: 'mob.reedback_snapper.claw_snap',
  mudSpit: 'mob.mire_croaker.mud_spit',
  pickSwing: 'mob.tallow_digger.pick_swing',
  candleFlare: 'mob.tallow_chanter.candle_flare',
  stoneSlam: 'mob.pale_sentinel.stone_slam',
  shadeBolt: 'mob.hollow_shade.shade_bolt',
} as const;

/** Mob auras bots/HUD may care about. */
export const MOB_AURA = {
  briarBleed: 'mob.briar_bandit.bleed',
  rallied: 'mob.bandit_captain.rallied',
} as const;

/** Hollow Barrow encounters. */
export const BARROW_ENCOUNTER = {
  bonecaller: 'barrow_bonecaller',
  king: 'barrow_king',
} as const;

/** Town NPCs (FieldNpcDef ids). */
export const TOWN_NPC = {
  elderRowan: 'elder_rowan',
  bountyBoard: 'bounty_board',
  smithHilda: 'smith_hilda',
  dungeonBoard: 'dungeon_board',
  quartermaster: 'quartermaster',
  townGuard: 'town_guard',
  villagerMira: 'villager_mira',
  villagerTobin: 'villager_tobin',
  /** Explains Auto and idle play (main quest step 7). */
  hearthkeeper: 'hearthkeeper',
} as const;

/** +zone: the zone's other NPCs (a fisher, lore stones). */
export const ZONE_NPC = {
  fisherDunn: 'fisher_dunn',
  loreLake: 'lore_lake',
  loreForest: 'lore_forest',
  loreRise: 'lore_rise',
  loreHeights: 'lore_heights',
  loreRoad: 'lore_road',
  loreField: 'lore_field',
  /** +detail: lore stones at the quiet country's points of interest. */
  loreElderTree: 'lore_eldertree',
  loreBeacon: 'lore_beacon',
  loreRing: 'lore_ring',
  loreStatue: 'lore_statue',
  loreShrine: 'lore_shrine',
  loreCamp: 'lore_camp',
  /** +glide: Granny Pell's note at the beginner sky puzzle (Hollowcrown Boughs). */
  loreSkyway: 'lore_skyway',
  outpostKeeper: 'outpost_keeper',
  /** The Lakeside Outpost's own bounty board (the lake and the heights). */
  outpostBoard: 'outpost_board',
} as const;

/** Field NPCs outside town (FieldNpcDef ids). */
export const FIELD_NPC = {
  /** At the Bramblewood overlook, facing the Tower: Mossvale's graduation step. */
  towerWarden: 'tower_warden',
} as const;

/** Portal ids (unique per map; `toPortal` names the arrival portal on the destination). */
export const PORTAL = {
  townToSunmeadow: 'to_sunmeadow',
  sunmeadowToTown: 'to_town',
  sunmeadowToBramblewood: 'to_bramblewood',
  barrowEntrance: 'barrow_entrance',
  bramblewoodToSunmeadow: 'to_sunmeadow',
} as const;

/** Field-only party auras (FieldDef.partyAuras): the solo damage boost for tanks and healers. */
export const FIELD_AURA = {
  soloTank: 'field.solo_tank',
  soloHealer: 'field.solo_healer',
} as const;
